import { useCallback, useEffect, useRef } from "react";
import { flushSync } from "react-dom";

import { usePlayground } from "@/components/Playground/playground-store";
import { usePlaygroundTourContext } from "@/components/Playground/tour/context";
import {
  PLAYGROUND_TOUR_STEP_INDICES,
  playgroundTourSteps,
} from "@/components/Playground/tour/constants";
import { scrollStepTargetIntoView } from "@/components/Playground/tour/helpers";
import { PLAYGROUND_DRAWER_MODES, PLAYGROUND_SESSION_MODES } from "@/components/Playground/types";
import { posthogTrackTourFinished } from "@/components/Playground/tour/posthog";

/**
 * Wires NextStepReact callbacks to the tour orchestration: scroll the
 * spotlight target, manage the popovers each step needs, and handle Esc
 * to exit.
 */
const TOUR_STEPS = playgroundTourSteps[0]?.steps ?? [];

export const usePlaygroundTourSteps = (removeTourParam: () => void) => {
  const tour = usePlaygroundTourContext();
  const playground = usePlayground();

  const handleStart = useCallback(() => {
    // Blur the trigger so the first Esc doesn't surface a focus-visible
    // ring on the now-irrelevant button.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, []);

  const handleStepChange = useCallback(
    (step: number) => {
      const isWorkflowStep =
        step >= PLAYGROUND_TOUR_STEP_INDICES.OUTPUT_WORKFLOW &&
        step <= PLAYGROUND_TOUR_STEP_INDICES.CHAT_WORKFLOW;
      const hasFakeOutput =
        step === PLAYGROUND_TOUR_STEP_INDICES.OUTPUT_RESULTS ||
        step === PLAYGROUND_TOUR_STEP_INDICES.OUTPUT_WORKFLOW;

      // Commit every layout-affecting change before NextStep advances its own
      // step. Its first position read then sees the final drawer, portal, and
      // target geometry instead of animating through intermediate states.
      flushSync(() => {
        if (step === PLAYGROUND_TOUR_STEP_INDICES.EVALUATE_WORKFLOW) {
          playground.setPlaygroundView(
            PLAYGROUND_SESSION_MODES.BATCH,
            PLAYGROUND_DRAWER_MODES.EVALUATE,
          );
        } else if (step === PLAYGROUND_TOUR_STEP_INDICES.CHAT_WORKFLOW) {
          playground.setPlaygroundView(
            PLAYGROUND_SESSION_MODES.CHAT,
            PLAYGROUND_DRAWER_MODES.OUTPUT,
          );
        } else if (
          step === PLAYGROUND_TOUR_STEP_INDICES.OUTPUT_WORKFLOW ||
          step === PLAYGROUND_TOUR_STEP_INDICES.MODEL_SELECTOR
        ) {
          playground.setPlaygroundView(
            PLAYGROUND_SESSION_MODES.BATCH,
            PLAYGROUND_DRAWER_MODES.OUTPUT,
          );
        }

        tour.setStep(step);
        tour.setHasFakeOutput(hasFakeOutput);
        tour.setModeSwitcherOpen(isWorkflowStep);
        tour.setModelPopoverOpen(step === PLAYGROUND_TOUR_STEP_INDICES.MODEL_SELECTOR);
      });

      scrollStepTargetIntoView(TOUR_STEPS[step]?.selector);
    },
    [playground, tour],
  );

  const resetTourView = useCallback(() => {
    playground.setPlaygroundView(PLAYGROUND_SESSION_MODES.BATCH, PLAYGROUND_DRAWER_MODES.OUTPUT);
  }, [playground]);

  const handleComplete = useCallback(() => {
    posthogTrackTourFinished();
    resetTourView();
    tour.end();
    removeTourParam();
  }, [resetTourView, tour, removeTourParam]);

  const handleSkip = useCallback(() => {
    resetTourView();
    tour.end();
    removeTourParam();
  }, [resetTourView, tour, removeTourParam]);

  // Refs let the listener stay attached for the bridge's lifetime without
  // reattaching on every tour state change.
  const tourRef = useRef(tour);
  tourRef.current = tour;
  const handleSkipRef = useRef(handleSkip);
  handleSkipRef.current = handleSkip;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }
      if (!tourRef.current.isActive) {
        return;
      }
      // Window capture + stopImmediatePropagation beats NextStepJS's
      // bubble-phase Esc handler so the tour exits on the first press.
      event.preventDefault();
      event.stopImmediatePropagation();
      handleSkipRef.current();
    };

    // Registered at mount so it lands in the queue before NextStepJS,
    // which only attaches its handler once the tour becomes visible.
    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () => window.removeEventListener("keydown", onKeyDown, { capture: true });
  }, []);

  return {
    handleStart,
    handleStepChange,
    handleComplete,
    handleSkip,
  };
};
